import {
  cloneElement,
  useCallback,
  useMemo,
  useRef,
  useState,
  useEffect,
  useContext,
} from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useVirtualizer } from '@tanstack/react-virtual';
import { useBreakpoint } from 'app/spacing';
import BasicTable from 'components/Tables/Basic';
import { TRow, NewTRow } from 'components/Kizen/Table';
import { useTranslation } from 'react-i18next';
import Loader from 'components/Kizen/Loader';
import { getSmallColumns } from './columns';
import {
  NoDataWrapper,
  StyledLoader,
  StyledTableScrollContainer,
  TableCard,
} from './styles';
import NoData from '../components/NoData';
import { getColumnsFromConfig, ROW_HEIGHT } from './util';
import { getInfiniteQueryForTable } from './getInfiniteQueryForTable';
import useMetadataForTable from './hooks/useMetadataForTable';
import { throttle } from 'lodash';
import {
  ScrollContainerBlocker,
  ScrollContainerBlockerWrapper,
} from 'components/Tables/ScrollContainerStyles';
import { PluginContext } from 'ts-components/Plugins/context';
import styled from '@emotion/styled';

export const CELL_PADDING = 30;
const DEFAULT_COLUMN_WIDTH = 200;

/**
 * Gets the default minimum width for a column.
 * Custom fields use bodyCell.minWidth, basic columns use cell.props.minWidth
 */
const getColumnDefaultWidth = (col) => {
  const minWidth = col.bodyCell?.minWidth ?? col.cell?.props?.minWidth;
  return minWidth ? parseInt(minWidth, 10) : DEFAULT_COLUMN_WIDTH;
};

const VirtualizedTable = styled(BasicTable)`
  thead {
    display: block;
    width: 100%;
    border-bottom: 1px solid var(--color-border-medium-light);
  }
  thead > tr > th {
    border-bottom: none;
  }
  tbody {
    width: 100%;
    display: block;
    position: relative;
    ${({ bodyHeight }) => bodyHeight && `height: ${bodyHeight}px;`}
  }
`;

const VirtualRow = styled(NewTRow)`
  position: absolute;
  top: 0;
  left: 0;
  display: table;
  height: ${ROW_HEIGHT}px;

  &:hover {
    background-color: var(--color-background-hover);
  }

  td {
    height: ${ROW_HEIGHT}px;

    > div {
      padding-right: 75px;
    }
  }
`;

const TableDashlet = ({
  dashlet,
  search: overallSearch,
  dashletSearch,
  model,
  contacts,
  filtersForCharts,
  onChangeColumnWidths,
  onChangeSort,
  onInfiniteQueryLoading,
  canEdit,
  persistentState,
}) => {
  const { t } = useTranslation();
  const isMobile = useBreakpoint();
  const objectId = model.id;
  const { dataAdornments } = useContext(PluginContext);

  const {
    fieldsById,
    fields,
    loading = true,
  } = useMetadataForTable({
    objectId,
    contacts,
  });

  const [columnWidths, setColumnWidths] = useState(
    dashlet?.config?.feExtraInfo?.columnWidths ?? {}
  );

  const throttleSetColumnWidths = useMemo(() => {
    return throttle((...args) => setColumnWidths(...args), 100);
  }, []);

  const onResizeColumnWidth = useCallback(
    ({ id, width }) => {
      throttleSetColumnWidths((prev) => ({ ...prev, [id]: width }));
    },
    [throttleSetColumnWidths]
  );

  const getColumnWidth = useCallback(
    (id) => {
      return columnWidths[id];
    },
    [columnWidths]
  );

  const onResizeStop = useCallback(() => {
    onChangeColumnWidths?.(columnWidths);
  }, [columnWidths, onChangeColumnWidths]);

  const columns = useMemo(() => {
    const internalColumns = getSmallColumns({
      columns: getColumnsFromConfig(dashlet, fieldsById),
      model,
      fieldsById,
      fields,
      t,
      contacts,
      onResizeColumnWidth,
      onResizeStop,
      getColumnWidth,
      resizable: canEdit,
      adornments: dataAdornments,
    });

    return internalColumns.map((col) => {
      const savedWidth = getColumnWidth(col.id);
      const width = parseInt(savedWidth, 10) || getColumnDefaultWidth(col);
      const totalCellWidth = width + CELL_PADDING;

      return {
        ...col,
        width: savedWidth,
        ...(col.bodyCell
          ? {
              bodyCell: {
                ...col.bodyCell,
                width: `${totalCellWidth}px`,
                minWidth: undefined,
                maxWidth: undefined,
              },
            }
          : col.cell
            ? {
                cell: cloneElement(col.cell, {
                  width: `${totalCellWidth}px`,
                  padding: '0px',
                  minWidth: undefined,
                  maxWidth: undefined,
                }),
              }
            : {}),
      };
    });
  }, [
    dataAdornments,
    dashlet,
    model,
    fieldsById,
    t,
    contacts,
    fields,
    onResizeColumnWidth,
    onResizeStop,
    getColumnWidth,
    canEdit,
  ]);

  const [sort, setSort] = useState(persistentState);

  const initialSort = useMemo(() => {
    const firstColumn = columns[0];
    if (!firstColumn) {
      return {};
    }

    const firstField = fieldsById[firstColumn.id];
    if (!firstField) {
      return {};
    }

    return {
      column: firstColumn.id,
      direction: 'asc',
      name: firstField.name,
      isDefault: firstField.isDefault,
    };
  }, [columns, fieldsById]);

  const fieldIds = getColumnsFromConfig(dashlet, fieldsById)
    .map((f) => f.id)
    .reduce((acc, id) => {
      if (id === 'fullName') {
        const { firstName, lastName } = model?.undeletableFields ?? {};
        acc.push(firstName.id, lastName.id);
        return acc;
      }
      acc.push(id);
      return acc;
    }, []);

  const search = useMemo(() => {
    if (overallSearch && dashletSearch) {
      return `${overallSearch} ${dashletSearch}`;
    } else if (dashletSearch) {
      return dashletSearch;
    }
    return overallSearch;
  }, [overallSearch, dashletSearch]);

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    isLoading,
  } = useInfiniteQuery(
    getInfiniteQueryForTable({
      objectId,
      search,
      dashlet,
      filtersForCharts,
      model,
      sort: sort ?? initialSort,
      fieldIds,
      searchWithinFieldIds: isMobile ? [] : fieldIds,
      loading,
    })
  );

  useEffect(() => {
    onInfiniteQueryLoading(isFetching);
  }, [isFetching, onInfiniteQueryLoading]);

  const allItems = useMemo(() => {
    return data?.pages?.flatMap((page) => page.results ?? []) ?? [];
  }, [data]);

  const scrollContainerRef = useRef(null);

  const virtualizer = useVirtualizer({
    count: allItems.length,
    getScrollElement: () => scrollContainerRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: 5,
  });

  const totalSize = virtualizer.getTotalSize();
  const virtualItems = virtualizer.getVirtualItems();

  // Handle infinite scrolling based on last virtual item index
  useEffect(() => {
    if (!hasNextPage || isFetchingNextPage) {
      return;
    }

    if (!virtualItems.length) {
      return;
    }

    const lastItem = virtualItems[virtualItems.length - 1];
    const lastIndex = allItems.length - 1;

    if (lastItem.index >= lastIndex) {
      fetchNextPage();
    }
  }, [
    virtualItems,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    allItems.length,
  ]);

  const messageHeadData = useMemo(
    () => ({
      meta: {
        sort: sort ?? initialSort,
        onSort: (column, direction) => {
          const field = fieldsById[column];
          setSort({
            column,
            direction,
            name: field.name,
            id: field.id,
            isDefault: field.isDefault,
          });
          onChangeSort({
            column,
            direction,
            name: field.name,
            id: field.id,
            isDefault: field.isDefault,
          });
        },
      },
    }),
    [sort, fieldsById, initialSort, onChangeSort]
  );

  if (loading) {
    return <Loader loading />;
  }

  return (
    <TableCard>
      <ScrollContainerBlockerWrapper className="scroll-container-blocker-wrapper">
        <ScrollContainerBlocker top="-1" zIndex="4" width="9" />
      </ScrollContainerBlockerWrapper>
      {allItems.length === 0 && !isLoading && (
        <NoDataWrapper>
          <NoData data-qa-dashlet-id={dashlet?.id} data-qa-no-data />
        </NoDataWrapper>
      )}
      <StyledTableScrollContainer
        className="StyledTableScrollContainer"
        ref={scrollContainerRef}
        left
        right
        bottom
        refresh={allItems.length}
        isTable
      >
        <VirtualizedTable
          stickyHeader
          staleData={!isFetchingNextPage && isFetching}
          head={<TRow head columns={columns} data={messageHeadData} />}
          bodyHeight={totalSize}
          forceDisplayBlock
          data-qa-chart="table"
          data-qa-chart-pending={isLoading}
          data-qa-dashlet-empty={allItems.length === 0}
        >
          {virtualItems.map((virtualItem) => {
            const item = allItems[virtualItem.index];
            return (
              <VirtualRow
                key={item.id}
                columns={columns}
                data={item}
                style={{ transform: `translateY(${virtualItem.start}px)` }}
              />
            );
          })}
        </VirtualizedTable>
        <StyledLoader
          loading={isFetchingNextPage || isFetching}
          noPadding={true}
        />
      </StyledTableScrollContainer>
    </TableCard>
  );
};

export default TableDashlet;
